/**
 * Immersive presentation mode. The presenter stands at the lectern and steps through a deck
 * (every dashboard, then every pinned clone); the current slide floats enlarged in front of
 * the presentation wall for everyone, the presenter's voice goes flat and loud, a spotlight
 * dims everything but the slide and the wall, the laser is the Point beam, and "Pull everyone
 * to me" asks each member (with a consent prompt) to step to the audience zone.
 */
import * as THREE from 'three';
import type { PinnedPanel, PresenterState, Slide } from '@clinspace/shared';
import type { Net } from '../app/net.ts';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import type { Dashboard, DashboardHost } from '../dashboards/Dashboard.ts';
import { PALETTE, chip, fillRoundRect, text, panelBackground } from '../dashboards/charts.ts';
import { createDashboard } from '../dashboards/index.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { HALL, PRESENTATION } from '../world/plan.ts';
import { audienceSpot, buildDeck, deckIndex, sameSlide, stepDeck } from './presenting.ts';

/** The lectern top: a tilted plate the presenter works from. */
/** The lectern stands beside the wall display (the audience is in front of the display). */
const LECTERN = { x: PRESENTATION.lectern.x, y: 1.23, z: PRESENTATION.lectern.z };
/** The slide floats just in front of the wall, sized for an audience one to three metres away. */
export const SLIDE_POSE = {
  x: PRESENTATION.x,
  y: PRESENTATION.y,
  z: HALL.front - 0.6,
  yaw: Math.PI,
  scale: 2.0,
};
const PROMPT_MS = 20_000;

interface LecternRegion {
  action?: 'start' | 'end' | 'prev' | 'next' | 'spotlight' | 'laser' | 'recentre' | 'clear';
  slide?: Slide;
}

export interface PresentationHandlers {
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  notice(lines: string[], ms?: number): void;
  /** Teleports the local user to feet (x, z) facing the wall. */
  goToAudience(x: number, z: number): void;
  togglePoint(): void;
  pointing(): boolean;
  pins(): PinnedPanel[];
  nonSpatial(sessionId: string, on: boolean): void;
  /** Called when the spotlight turns on or off, so the wall can stay bright. */
  spotlight(on: boolean): void;
}

export class Presentation {
  readonly group = new THREE.Group();
  readonly lectern: CanvasPanel;
  readonly prompt: CanvasPanel;
  /** The slide clone everyone sees (undefined when nothing is shown). */
  slide: Dashboard | undefined;
  presenter: PresenterState | null = null;
  pendingRecentre: { from: string; at: number } | undefined;
  private readonly spot: THREE.Mesh;
  private shownSlide: Slide | null = null;

  constructor(
    private readonly net: Net,
    private readonly host: DashboardHost,
    private readonly interactive: InteractiveSystem,
    camera: THREE.Camera,
    private readonly handlers: PresentationHandlers,
  ) {
    this.group.name = 'presentation';
    this.lectern = new CanvasPanel({
      width: 0.6,
      height: 0.42,
      pixelWidth: 600,
      pixelHeight: 420,
      name: 'lectern',
      resolution: 1.5,
    });
    const stand = new THREE.Group();
    stand.position.set(LECTERN.x, LECTERN.y, LECTERN.z);
    // The presenter stands between the lectern and the entrance wall (its top faces +z).
    stand.rotation.y = PRESENTATION.lectern.yaw;
    this.lectern.mesh.rotation.x = -1.15;
    stand.add(this.lectern.mesh);
    this.group.add(stand);
    this.lectern.setDraw((ctx, panel) => this.drawLectern(ctx, panel));
    this.lectern.onSelect = (region) => this.selectLectern(region);
    this.lectern.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.lectern.attach(interactive);

    // The consent prompt hangs in front of the camera until answered or timed out.
    this.prompt = new CanvasPanel({
      width: 0.62,
      height: 0.24,
      pixelWidth: 620,
      pixelHeight: 240,
      name: 'recentre-prompt',
      resolution: 1.5,
    });
    this.prompt.mesh.position.set(0, 0.1, -0.85);
    this.prompt.mesh.renderOrder = 70;
    (this.prompt.mesh.material as THREE.MeshBasicMaterial).depthTest = false;
    this.prompt.mesh.visible = false;
    this.prompt.setDraw((ctx, panel) => this.drawPrompt(ctx, panel));
    this.prompt.onSelect = (region) => {
      if (region.id === 'join') this.acceptRecentre();
      else this.declineRecentre();
    };
    this.prompt.attach(interactive);
    camera.add(this.prompt.mesh);

    this.spot = new THREE.Mesh(
      new THREE.SphereGeometry(30, 16, 12),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.74,
        side: THREE.BackSide,
        depthWrite: false,
        // Drawn over everything regardless of distance; the slide and the wall then draw on top.
        depthTest: false,
      }),
    );
    this.spot.name = 'spotlight';
    this.spot.renderOrder = 500;
    this.spot.frustumCulled = false;
    this.spot.visible = false;
    this.group.add(this.spot);
  }

  get presenting(): boolean {
    return !!this.presenter && this.presenter.sessionId === this.net.sessionId;
  }

  get spotlightOn(): boolean {
    return !!this.presenter?.spotlight;
  }

  get promptOpen(): boolean {
    return this.prompt.mesh.visible;
  }

  /* ---------- Presenter actions ---------- */

  toggle(): void {
    if (this.presenting) this.net.presentEnd();
    else if (this.presenter)
      this.handlers.notice(['Not now', `${this.presenter.name} is presenting`], 2500);
    else this.net.presentStart();
  }

  step(dir: 1 | -1): void {
    if (!this.presenting) return;
    const next = stepDeck(buildDeck(this.handlers.pins()), this.presenter!.slide, dir);
    this.net.presentSlide(next);
  }

  setSlide(slide: Slide | null): void {
    if (this.presenting) this.net.presentSlide(slide);
  }

  setSpotlight(on: boolean): void {
    if (this.presenting) this.net.presentSpotlight(on);
  }

  recentre(): void {
    if (!this.presenting) return;
    this.net.presentRecentre();
    this.handlers.notice(
      ['Asked everyone to join you', 'Each person gets a prompt and may decline.'],
      3000,
    );
  }

  /* ---------- Room events ---------- */

  onState(state: PresenterState | null): void {
    const before = this.presenter;
    this.presenter = state;
    if (before && before.sessionId !== state?.sessionId)
      this.handlers.nonSpatial(before.sessionId, false);
    if (state && before?.sessionId !== state.sessionId) {
      this.handlers.nonSpatial(state.sessionId, true);
      if (state.sessionId !== this.net.sessionId)
        this.handlers.notice(
          [`${state.name} is presenting`, 'Slides appear at the presentation wall.'],
          3500,
        );
    } else if (!state && before) {
      if (before.sessionId !== this.net.sessionId)
        this.handlers.notice([`${before.name} ended the presentation`], 2500);
    }
    this.showSlide(state?.slide ?? null);
    const spotlight = !!state?.spotlight;
    if (this.spot.visible !== spotlight) {
      this.spot.visible = spotlight;
      this.handlers.spotlight(spotlight);
      this.applySlideSpotlight(spotlight);
    }
    this.lectern.invalidate();
  }

  onRecentre(from: { sessionId: string; name: string }): void {
    this.pendingRecentre = { from: from.name, at: performance.now() };
    this.prompt.mesh.visible = true;
    this.prompt.invalidate();
  }

  acceptRecentre(): void {
    if (!this.pendingRecentre) return;
    const spot = audienceSpot(this.net.sessionId ?? 'me');
    this.handlers.goToAudience(spot.x, spot.z);
    this.pendingRecentre = undefined;
    this.prompt.mesh.visible = false;
  }

  declineRecentre(): void {
    this.pendingRecentre = undefined;
    this.prompt.mesh.visible = false;
  }

  /** The pins changed: a pin slide may have gone, the deck on the lectern is different. */
  refreshPins(): void {
    this.lectern.invalidate();
    if (
      this.shownSlide?.kind === 'pin' &&
      !this.handlers.pins().some((p) => p.id === (this.shownSlide as { id: string }).id)
    )
      this.showSlide(null);
  }

  private showSlide(slide: Slide | null): void {
    if (sameSlide(slide, this.shownSlide) && (slide === null || this.slide)) return;
    this.shownSlide = slide;
    if (this.slide) {
      this.group.remove(this.slide.group);
      this.slide.dispose();
      this.slide = undefined;
    }
    if (!slide) return;
    let dashboard: Dashboard;
    if (slide.kind === 'pin') {
      const pin = this.handlers.pins().find((p) => p.id === slide.id);
      if (!pin) return;
      dashboard = createDashboard(pin.dashboard, this.host);
      dashboard.filters = { ...pin.state.filters };
      dashboard.path = [...pin.state.path];
    } else {
      dashboard = createDashboard(slide.dashboard, this.host);
      dashboard.filters = { ...slide.filters };
      dashboard.path = [...slide.path];
    }
    dashboard.panel.setResolution(1.5);
    dashboard.home.position.set(SLIDE_POSE.x, SLIDE_POSE.y, SLIDE_POSE.z);
    dashboard.home.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), SLIDE_POSE.yaw);
    dashboard.home.scale = SLIDE_POSE.scale;
    dashboard.goHome();
    dashboard.invalidate();
    dashboard.attach();
    this.group.add(dashboard.group);
    this.slide = dashboard;
    this.applySlideSpotlight(this.spot.visible);
  }

  private applySlideSpotlight(on: boolean): void {
    if (!this.slide) return;
    const m = this.slide.panel.mesh.material as THREE.MeshBasicMaterial;
    m.depthTest = !on;
    m.transparent = on;
    m.needsUpdate = true;
    this.slide.panel.mesh.renderOrder = on ? 600 : 0;
  }

  /* ---------- Per frame ---------- */

  update(head: THREE.Vector3, now = performance.now()): void {
    this.spot.position.copy(head);
    if (this.pendingRecentre && now - this.pendingRecentre.at > PROMPT_MS) this.declineRecentre();
    this.lectern.update(now);
    this.prompt.update(now);
    this.slide?.update();
  }

  /* ---------- Lectern ---------- */

  private drawLectern(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 16);
    text(ctx, 'LECTERN', 20, 32, { size: 15, weight: 700, colour: PALETTE.teal });
    const p = this.presenter;
    const mine = this.presenting;
    const deck = buildDeck(this.handlers.pins());
    const index = p ? deckIndex(deck, p.slide) : -1;
    text(
      ctx,
      !p
        ? 'Nobody is presenting'
        : mine
          ? `You are presenting · slide ${index < 0 ? '-' : index + 1} of ${deck.length}`
          : `${p.name} is presenting · slide ${index < 0 ? '-' : index + 1} of ${deck.length}`,
      W - 20,
      32,
      { size: 12, colour: PALETTE.muted, align: 'right', maxWidth: W - 140 },
    );
    const button = (
      id: string,
      label: string,
      x: number,
      y: number,
      data: LecternRegion,
      tip: string[],
      opts: { active?: boolean; colour?: string; minW?: number } = {},
    ) => {
      const r = chip(ctx, label, x, y, {
        active: opts.active,
        hovered: panel.isHovered(id),
        h: 34,
        size: 13,
        colour: opts.colour,
        minW: opts.minW,
      });
      panel.region<LecternRegion>({ id, ...r, data, tooltip: tip });
      return r.w;
    };
    if (!mine) {
      button(
        'start',
        p ? `${p.name} has the lectern` : 'Present',
        20,
        52,
        p ? {} : { action: 'start' },
        p
          ? ['Wait for the presenter to finish']
          : ['Start presenting', 'Your voice goes to everyone at full volume'],
        { active: !p, minW: 120 },
      );
      text(
        ctx,
        'Slides float in front of the presentation wall. The presenter can ask everyone',
        20,
        120,
        {
          size: 11,
          colour: PALETTE.muted,
        },
      );
      text(ctx, 'to join them; you will get a prompt and may decline.', 20, 138, {
        size: 11,
        colour: PALETTE.muted,
      });
      if (p?.slide) {
        const label = deck[index]?.label ?? 'a slide';
        text(ctx, `Now showing: ${label}`, 20, 176, { size: 13, weight: 600, maxWidth: W - 40 });
      }
      return;
    }
    let x = 20;
    x +=
      button('end', 'End', x, 52, { action: 'end' }, ['End the presentation'], {
        colour: PALETTE.crimson,
        active: true,
      }) + 8;
    x += button('prev', '‹ Prev', x, 52, { action: 'prev' }, ['Previous slide ([ on desktop)']) + 8;
    x += button('next', 'Next ›', x, 52, { action: 'next' }, ['Next slide (] on desktop)']) + 8;
    button('clear', 'Blank', x, 52, { action: 'clear' }, ['Take the slide down']);
    x = 20;
    x +=
      button(
        'spotlight',
        p!.spotlight ? 'Spotlight on' : 'Spotlight',
        x,
        96,
        { action: 'spotlight' },
        ['Dims everything but the slide and the wall'],
        { active: p!.spotlight },
      ) + 8;
    x +=
      button(
        'laser',
        this.handlers.pointing() ? 'Laser on' : 'Laser',
        x,
        96,
        { action: 'laser' },
        ['Your pointer ray is drawn for everyone (Point mode)'],
        { active: this.handlers.pointing() },
      ) + 8;
    button('recentre', 'Pull everyone to me', x, 96, { action: 'recentre' }, [
      'Everyone gets a prompt to step to the audience zone',
    ]);
    text(ctx, 'Slides', 20, 156, { size: 11, colour: PALETTE.muted });
    x = 20;
    let y = 166;
    deck.forEach((entry, i) => {
      ctx.font = `600 ${Math.round(11 * 1.3)}px sans-serif`;
      const w = Math.min(190, ctx.measureText(entry.label).width + 20);
      if (x + w > W - 20) {
        x = 20;
        y += 32;
      }
      if (y > H - 30) return;
      const id = `slide:${i}`;
      const r = chip(
        ctx,
        entry.label.length > 24 ? `${entry.label.slice(0, 23)}…` : entry.label,
        x,
        y,
        {
          active: i === index,
          hovered: panel.isHovered(id),
          h: 26,
          size: 10,
        },
      );
      panel.region<LecternRegion>({
        id,
        ...r,
        data: { slide: entry.slide },
        tooltip: [entry.label, 'Show this slide'],
      });
      x += r.w + 6;
    });
  }

  private selectLectern(region: Region): void {
    const data = region.data as LecternRegion | undefined;
    if (!data) return;
    if (data.slide) this.setSlide(data.slide);
    switch (data.action) {
      case 'start':
      case 'end':
        this.toggle();
        break;
      case 'prev':
        this.step(-1);
        break;
      case 'next':
        this.step(1);
        break;
      case 'clear':
        this.setSlide(null);
        break;
      case 'spotlight':
        this.setSpotlight(!this.presenter?.spotlight);
        break;
      case 'laser':
        this.handlers.togglePoint();
        this.lectern.invalidate();
        break;
      case 'recentre':
        this.recentre();
        break;
    }
  }

  private drawPrompt(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 18, PALETTE.surface);
    ctx.strokeStyle = PALETTE.teal;
    ctx.lineWidth = 3;
    ctx.strokeRect(2, 2, W - 4, H - 4);
    text(ctx, `${this.pendingRecentre?.from ?? 'The presenter'} asks everyone to gather`, 24, 50, {
      size: 17,
      weight: 700,
      maxWidth: W - 48,
    });
    text(
      ctx,
      'Join to step to the audience zone by the presentation wall. You can stay where you are.',
      24,
      84,
      {
        size: 12,
        colour: PALETTE.muted,
        maxWidth: W - 48,
      },
    );
    const join = chip(ctx, 'Join', 24, 140, {
      active: true,
      hovered: panel.isHovered('join'),
      h: 48,
      size: 16,
      minW: 150,
    });
    panel.region({ id: 'join', ...join, tooltip: ['Teleport to the audience zone'] });
    const stay = chip(ctx, 'Stay here', 200, 140, {
      hovered: panel.isHovered('stay'),
      h: 48,
      size: 16,
      minW: 150,
    });
    panel.region({ id: 'stay', ...stay, tooltip: ['Dismiss'] });
  }

  dispose(): void {
    if (this.presenter) this.handlers.nonSpatial(this.presenter.sessionId, false);
    this.lectern.detach(this.interactive);
    this.lectern.dispose();
    this.prompt.detach(this.interactive);
    this.prompt.mesh.parent?.remove(this.prompt.mesh);
    this.prompt.dispose();
    if (this.slide) {
      this.slide.dispose();
      this.slide = undefined;
    }
    this.spot.geometry.dispose();
    (this.spot.material as THREE.Material).dispose();
  }
}
